import type { MetricUnit } from './types.js'
import type { Provider } from './providers.js'

export interface MetricDefinition {
  key: string
  label: string
  unit: MetricUnit
  /** Higher is better. Drives the colour of a delta. */
  positive: boolean
  /** Networks that report it. */
  providers: readonly Provider[]
  hint: string
}

const all: readonly Provider[] = [
  'facebook',
  'instagram',
  'tiktok',
  'gbp',
  'youtube',
  'linkedin',
  'bluesky',
  'pinterest',
  'threads',
  'x',
]

export const METRICS: readonly MetricDefinition[] = [
  {
    key: 'followers',
    label: 'Followers',
    unit: 'count',
    positive: true,
    providers: [
      'facebook',
      'instagram',
      'tiktok',
      'linkedin',
      'bluesky',
      'pinterest',
      'threads',
      'x',
    ],
    hint: 'People following the account at the end of the range.',
  },
  {
    key: 'subscribers',
    label: 'Subscribers',
    unit: 'count',
    positive: true,
    providers: ['youtube'],
    hint: 'Channel subscribers at the end of the range.',
  },
  {
    key: 'reach',
    label: 'Reach',
    unit: 'count',
    positive: true,
    providers: ['facebook', 'instagram', 'linkedin'],
    hint: 'Unique accounts that saw any content.',
  },
  {
    key: 'impressions',
    label: 'Impressions',
    unit: 'count',
    positive: true,
    /*
     * X reports `impression_count` **per post and as a lifetime total**, never as a daily account
     * series — it has no account-analytics endpoint at all — so its impressions appear on the post
     * deep dive rather than on a timeline. See `backend/src/providers/x/map.ts`.
     */
    providers: ['facebook', 'instagram', 'linkedin', 'pinterest', 'x'],
    hint: 'Total times content was displayed.',
  },
  {
    key: 'views',
    label: 'Views',
    unit: 'count',
    positive: true,
    providers: ['tiktok', 'youtube', 'gbp', 'threads'],
    hint: 'Times the content or profile was viewed.',
  },
  {
    key: 'engagement',
    label: 'Engagement',
    unit: 'percent',
    positive: true,
    providers: ['facebook', 'instagram', 'tiktok', 'youtube', 'linkedin'],
    hint: 'Interactions divided by reach.',
  },
  {
    key: 'posts',
    label: 'Posts',
    unit: 'count',
    positive: true,
    providers: all,
    hint: 'Content published in the range.',
  },
  {
    key: 'saves',
    label: 'Saves',
    unit: 'count',
    positive: true,
    /* X's `bookmark_count` is the same gesture under another word, and is reported here. */
    providers: ['instagram', 'pinterest', 'x'],
    hint: 'Times a post was saved.',
  },
  {
    key: 'shares',
    label: 'Shares',
    unit: 'count',
    positive: true,
    providers: ['facebook', 'instagram', 'tiktok', 'linkedin', 'bluesky', 'threads'],
    hint: 'Times content was shared onward.',
  },
  {
    key: 'watchTime',
    label: 'Watch time',
    unit: 'duration',
    positive: true,
    providers: ['tiktok', 'youtube'],
    hint: 'Total minutes watched.',
  },
  {
    key: 'ctr',
    label: 'Click-through rate',
    unit: 'percent',
    positive: true,
    providers: ['youtube'],
    hint: 'Impressions that turned into a view.',
  },
  {
    key: 'clicks',
    label: 'Link clicks',
    unit: 'count',
    positive: true,
    providers: ['facebook', 'instagram', 'linkedin', 'pinterest'],
    hint: 'Clicks on links in content or bio.',
  },
  {
    key: 'searches',
    label: 'Searches',
    unit: 'count',
    positive: true,
    providers: ['gbp'],
    hint: 'Searches that surfaced the business.',
  },
  {
    key: 'calls',
    label: 'Calls',
    unit: 'count',
    positive: true,
    providers: ['gbp'],
    hint: 'Calls started from the listing.',
  },
  {
    key: 'directions',
    label: 'Direction requests',
    unit: 'count',
    positive: true,
    providers: ['gbp'],
    hint: 'Route requests to the business.',
  },
  {
    key: 'reviews',
    label: 'Reviews',
    unit: 'count',
    positive: true,
    providers: ['gbp'],
    hint: 'Reviews received in the range.',
  },
  {
    key: 'rating',
    label: 'Rating',
    unit: 'rating',
    positive: true,
    providers: ['gbp'],
    hint: 'Average star rating.',
  },
]

export const METRIC_BY_KEY: Record<string, MetricDefinition> = Object.fromEntries(
  METRICS.map((m) => [m.key, m]),
)

export const metricsFor = (provider: Provider): MetricDefinition[] =>
  METRICS.filter((m) => (m.providers as readonly Provider[]).includes(provider))

/** The metric each network leads with on the dashboard tile. */
export const HEADLINE_METRIC: Partial<Record<Provider, string>> = {
  facebook: 'reach',
  instagram: 'reach',
  tiktok: 'views',
  gbp: 'views',
  youtube: 'views',
  linkedin: 'impressions',
  bluesky: 'followers',
  pinterest: 'impressions',
  threads: 'views',
  /*
   * `followers`, not `impressions`, and for Bluesky's reason: the dashboard tile leads with a
   * number that has a *timeline* behind it, and on X the only account series is the follower
   * reading. Impressions are real and are charted per post, which is a different screen.
   */
  x: 'followers',
}

export const formatMetric = (value: number, unit: MetricUnit, locale = 'it-IT'): string => {
  switch (unit) {
    case 'percent':
      return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)}%`
    case 'currency':
      return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value)
    case 'rating':
      return new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)
    case 'duration': {
      const h = Math.floor(value / 60)
      const m = Math.round(value % 60)
      return h > 0 ? `${h}h ${m}m` : `${m}m`
    }
    default:
      return new Intl.NumberFormat(locale, { notation: value >= 10_000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(value)
  }
}

export const formatDelta = (deltaPct: number): string =>
  `${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(1)}%`
